import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Archive, ArchiveRestore, Check, LucideAngularModule, Plus, X } from 'lucide-angular';
import { NotificationService } from '@app/shared/notification/notification.service';
import { EventRoleNameService } from '@app/events/shared/event-role-name.service';
import { EventRoleName, EventRoleNameRole } from '@app/events/shared/event-role-name.model';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';

interface RoleList {
  role: EventRoleNameRole;
  label: string;
  helper: string;
  /** TVG-312: lists that belong to the event ownership block follow its toggle. */
  requiresOwnershipToggle: boolean;
  entries: EventRoleName[];
  newName: string;
  saving: boolean;
}

/**
 * TVG-83 / TVG-312: Admin management block for the Sales Manager, Show around
 * and lead source lists, shown on the Users page. Admins add entries, rename
 * them, and retire the ones no longer in use — a retired entry stays on
 * historic records but is no longer offered on new ones. Entries are never
 * hard-deleted.
 *
 * The two ownership lists only appear when the event-ownership-fields toggle is
 * on (their API 404s otherwise); the lead source list is always shown.
 */
@Component({
  selector: 'app-event-role-names-manager',
  imports: [FormsModule, LucideAngularModule],
  templateUrl: './event-role-names-manager.component.html',
})
export class EventRoleNamesManagerComponent implements OnInit {
  protected readonly PlusIcon = Plus;
  protected readonly ArchiveIcon = Archive;
  protected readonly RestoreIcon = ArchiveRestore;
  protected readonly CheckIcon = Check;
  protected readonly CancelIcon = X;

  protected editingId: number | null = null;
  protected editingName = '';

  protected lists: RoleList[] = [];

  private readonly allLists: RoleList[] = [
    {
      role: 'sales_manager',
      label: 'Sales Manager names',
      helper: 'Who sold the booking. Locks on confirmation and stays on historic events after a leaver is deactivated.',
      requiresOwnershipToggle: true,
      entries: [],
      newName: '',
      saving: false,
    },
    {
      role: 'show_around',
      label: 'Show around names',
      helper: 'Who carried out the show around / viewing. Internal field, not shown to clients.',
      requiresOwnershipToggle: true,
      entries: [],
      newName: '',
      saving: false,
    },
    {
      role: 'lead_source',
      label: 'Lead sources',
      helper: 'Where the enquiry came from. Retired sources stay on past contacts but are no longer offered on new ones.',
      requiresOwnershipToggle: false,
      entries: [],
      newName: '',
      saving: false,
    },
  ];

  constructor(
    private eventRoleNameService: EventRoleNameService,
    private notificationService: NotificationService,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit(): void {
    const ownershipEnabled = this.featureToggleService.isEnabled('event-ownership-fields');
    this.lists = this.allLists.filter((list) => !list.requiresOwnershipToggle || ownershipEnabled);
    this.lists.forEach((list) => this.load(list));
  }

  private load(list: RoleList): void {
    this.eventRoleNameService.getByRole(list.role, true).subscribe({
      next: (res) => {
        list.entries = res.data;
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to load names');
      },
    });
  }

  protected add(list: RoleList): void {
    const name = list.newName.trim();
    if (!name || list.saving) {
      return;
    }

    list.saving = true;
    this.eventRoleNameService.create(list.role, name).subscribe({
      next: () => {
        list.newName = '';
        list.saving = false;
        this.load(list);
      },
      error: (error) => {
        list.saving = false;
        this.notificationService.error(error.error?.message || 'Failed to add name');
      },
    });
  }

  protected toggleActive(list: RoleList, entry: EventRoleName): void {
    this.eventRoleNameService.update(entry.id, { is_active: !entry.is_active }).subscribe({
      next: () => this.load(list),
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to update name');
      },
    });
  }

  protected startEdit(entry: EventRoleName): void {
    this.editingId = entry.id;
    this.editingName = entry.name;
  }

  protected cancelEdit(): void {
    this.editingId = null;
    this.editingName = '';
  }

  protected saveEdit(list: RoleList, entry: EventRoleName): void {
    const name = this.editingName.trim();
    if (!name) {
      return;
    }

    this.eventRoleNameService.update(entry.id, { name }).subscribe({
      next: () => {
        this.cancelEdit();
        this.load(list);
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to rename');
      },
    });
  }
}
